import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, User, Clock, Video, Repeat, Lock } from "lucide-react";
import { isVirtualEvent } from "@/lib/events";
import { fmtTimeRange, money } from "@/lib/format";
import { expandOccurrences } from "@/lib/occurrences";

const CATEGORY_COLORS = {
  Circle: "#144F75",
  "Sound Healing": "#8FA89B",
  Breathwork: "#D98E73",
  "Energy Healing": "#6B7F9E",
  Meditation: "#5C8A72",
  Workshop: "#B5794E",
  Class: "#3A7CA5",
  Retreat: "#9E5C7F",
};
const colorFor = (c) => CATEGORY_COLORS[c] || "#144F75";

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay(); // 0 Sun
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ScheduleGrid({ events = [], onEventClick }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const byDay = useMemo(() => {
    const map = {};
    days.forEach((d) => (map[d.toISOString().slice(0, 10)] = []));
    expandOccurrences(events).forEach((o) => { if (map[o.date]) map[o.date].push(o); });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [days, events]);

  const rangeLabel = `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  const todayKey = new Date().toISOString().slice(0, 10);

  const shift = (n) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + n * 7);
    setWeekStart(d);
  };

  return (
    <div data-testid="schedule-grid">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl text-[#144F75]">{rangeLabel}</h3>
        <div className="flex items-center gap-2">
          <button data-testid="schedule-today-btn" onClick={() => setWeekStart(startOfWeek(new Date()))} className="rounded-full border border-[#E5E0D8] px-4 py-2 text-sm text-[#4A5D6B] hover:border-[#144F75]">Today</button>
          <button data-testid="schedule-prev-btn" onClick={() => shift(-1)} className="grid place-items-center h-9 w-9 rounded-full border border-[#E5E0D8] hover:border-[#144F75] text-[#144F75]"><ChevronLeft size={18} /></button>
          <button data-testid="schedule-next-btn" onClick={() => shift(1)} className="grid place-items-center h-9 w-9 rounded-full border border-[#E5E0D8] hover:border-[#144F75] text-[#144F75]"><ChevronRight size={18} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {days.map((d) => {
          const key = d.toISOString().slice(0, 10);
          const items = byDay[key] || [];
          const isToday = key === todayKey;
          return (
            <div key={key} data-testid={`schedule-day-${key}`} className={`rounded-2xl border p-3 min-h-[160px] ${isToday ? "border-[#144F75] bg-[#144F75]/[0.04]" : "border-[#E5E0D8] bg-white"}`}>
              <div className="text-center mb-3">
                <div className="eyw-label !tracking-widest">{DAY_LABELS[d.getDay()]}</div>
                <div className={`font-serif text-2xl ${isToday ? "text-[#D98E73]" : "text-[#1A242B]"}`}>{d.getDate()}</div>
              </div>
              <div className="space-y-2">
                {items.length === 0 && <p className="text-xs text-[#4A5D6B]/50 text-center py-2">—</p>}
                {items.map((e) => (
                  e.is_placeholder ? (
                    <div key={e.id} data-testid={`schedule-placeholder-${e.id}`}
                      className="rounded-lg bg-[#144F75]/[0.05] border border-dashed border-[#144F75]/25 p-2.5">
                      <div className="text-[13px] font-semibold text-[#144F75] flex items-center gap-1"><Lock size={11} /> Member Event</div>
                      <div className="flex items-center gap-1 text-[11px] text-[#4A5D6B] mt-1"><Clock size={11} /> {fmtTimeRange(e.start_time, e.end_time)}</div>
                      <div className="text-[10px] text-[#8FA89B] mt-0.5">Reserved · access required</div>
                    </div>
                  ) : (
                  <div
                    key={e.id}
                    data-testid={`schedule-event-${e.id}`}
                    onClick={() => onEventClick && onEventClick(e)}
                    className={`rounded-lg bg-[#F9F7F3] border-l-4 p-2.5 hover:shadow-sm transition-shadow ${onEventClick ? "cursor-pointer hover:bg-[#144F75]/[0.06]" : ""} ${e.cancelled ? "opacity-60" : ""}`}
                    style={{ borderColor: colorFor(e.category) }}
                  >
                    <div className={`text-[13px] font-semibold leading-tight ${e.cancelled ? "text-[#4A5D6B] line-through" : "text-[#1A242B]"}`}>{e.title}</div>
                    {e.access_level && e.access_level !== "public" && (
                      <div data-testid="event-access-badge" className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide mt-0.5 px-1.5 py-0.5 rounded-full bg-[#144F75]/[0.08] text-[#144F75]"><Lock size={8} /> Members</div>
                    )}
                    <div className="flex items-center gap-1 text-[11px] text-[#4A5D6B] mt-1">
                      <Clock size={11} /> {fmtTimeRange(e.start_time, e.end_time)}
                    </div>
                    {e.cancelled && (
                      <span data-testid={`schedule-cancelled-${e.id}`} className="inline-flex items-center mt-1 rounded-full bg-red-100 text-red-600 px-1.5 py-0.5 text-[10px] font-semibold">Canceled</span>
                    )}
                    {e._multiday && (
                      <span data-testid={`schedule-multiday-${e.id}`} className="inline-flex items-center gap-1 mt-1 rounded-full bg-[#9E5C7F]/15 text-[#9E5C7F] px-1.5 py-0.5 text-[10px] font-semibold">
                        Multi-day
                      </span>
                    )}
                    {e.series_id && !e._multiday && (
                      <span data-testid={`schedule-repeats-${e.id}`} className="inline-flex items-center gap-1 mt-1 rounded-full bg-[#144F75]/12 text-[#144F75] px-1.5 py-0.5 text-[10px] font-semibold">
                        <Repeat size={9} /> Repeats
                      </span>
                    )}
                    <div className="flex items-center gap-1 text-[11px] text-[#4A5D6B]">
                      <MapPin size={11} /> {e.location}
                    </div>
                    {isVirtualEvent(e) && (
                      <span data-testid={`schedule-online-${e.id}`} className="inline-flex items-center gap-1 mt-1 rounded-full bg-[#144F75]/10 text-[#144F75] px-1.5 py-0.5 text-[10px] font-semibold">
                        <Video size={10} /> Online
                      </span>
                    )}
                    {e.facilitator && (
                      <div className="flex items-center gap-1 text-[11px] text-[#4A5D6B]">
                        <User size={11} /> {e.facilitator}
                      </div>
                    )}
                    {e.center && (
                      <span className={`inline-block mt-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${e.center.kind === "flagship" ? "bg-[#144F75]/10 text-[#144F75]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>
                        {e.center.kind === "flagship" ? "EYW Flagship" : "Affiliate"}
                      </span>
                    )}
                    <div className="mt-1.5 text-[11px] font-medium" style={{ color: colorFor(e.category) }}>
                      {e.member_included ? "Member included" : (e.tiers || []).length > 0 ? (() => { const today = new Date().toISOString().slice(0, 10); const open = e.tiers.filter((t) => !t.closes_on || t.closes_on >= today); const src = open.length ? open : e.tiers; return `From ${money(Math.min(...src.map((t) => Number(t.price) || 0)))}`; })() : e.price > 0 ? `${money(e.price)}` : "Free"}
                    </div>
                    {e.is_full ? (
                      <span data-testid={`schedule-full-${e.id}`} className="inline-flex mt-1 rounded-full bg-[#B5794E]/15 text-[#B5794E] px-1.5 py-0.5 text-[10px] font-semibold">{e.waitlist_enabled === false ? "Fully booked" : "Full · join waitlist"}</span>
                    ) : e.spots_left != null && e.spots_left <= 5 ? (
                      <span data-testid={`schedule-spots-${e.id}`} className="inline-flex mt-1 rounded-full bg-[#D98E73]/15 text-[#B5794E] px-1.5 py-0.5 text-[10px] font-semibold">{e.spots_left} spot{e.spots_left === 1 ? "" : "s"} left</span>
                    ) : null}
                    {onEventClick && !e.cancelled && (
                      <span data-testid={`schedule-cta-${e.id}`} className="mt-1.5 inline-flex items-center text-[11px] font-semibold text-[#144F75]">{e.is_full ? "View & join waitlist →" : "Register →"}</span>
                    )}
                  </div>
                  )
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { colorFor };
